import { computed, Injectable, signal } from '@angular/core';
import { UtilLocalStorage } from '../../shared/storage/local-storage';
import { LoginResponse, AuthenticatedUser, MenuItem, RolePermissions } from '../../domain/models/session/session';

@Injectable({ providedIn: 'root' })
export class SessionCtl {
  private readonly sessionKey = 'c2Vzc2lvbg';
  private readonly lastUserKey = 'bGFzdFVzZXI';
  private readonly rolePermissionsKey = 'cm9sZVBlcm1pc3Npb25z';

  private readonly sessionSignal = signal<LoginResponse | null>(null);
  readonly session = this.sessionSignal.asReadonly();

  private readonly rolePermissionsSignal = signal<RolePermissions | null>(null);
  readonly rolePermissions = this.rolePermissionsSignal.asReadonly();

  readonly canRead = computed(() => this.rolePermissionsSignal()?.allowRead ?? false);
  readonly canWrite = computed(() =>
    (this.rolePermissionsSignal()?.allowCreate ?? false) ||
    (this.rolePermissionsSignal()?.allowUpdate ?? false)
  );
  readonly canCreate = computed(() => this.rolePermissionsSignal()?.allowCreate ?? false);
  readonly canUpdate = computed(() => this.rolePermissionsSignal()?.allowUpdate ?? false);
  readonly canDelete = computed(() => this.rolePermissionsSignal()?.allowDelete ?? false);

  constructor() {
    this.hydrateFromStorage();
  }

  hydrateFromStorage(): void {
    const stored = this.sessionItem();
    if (stored) this.sessionSignal.set(stored);

    const storedPermissions = UtilLocalStorage.item<RolePermissions>(this.rolePermissionsKey);
    if (storedPermissions) this.rolePermissionsSignal.set(storedPermissions);
  }

  sessionSet(session: LoginResponse): void {
    UtilLocalStorage.itemSet<LoginResponse>(this.sessionKey, session);
    this.sessionSignal.set(session);
  }

  setRolePermissions(permissions: RolePermissions): void {
    UtilLocalStorage.itemSet<RolePermissions>(this.rolePermissionsKey, permissions);
    this.rolePermissionsSignal.set(permissions);
  }

  sessionItem(): LoginResponse | undefined {
    return UtilLocalStorage.item<LoginResponse>(this.sessionKey);
  }

  sessionClear(): void {
    const currentUsername = this.getUserDisplayName();
    if (currentUsername) {
      this.saveLastUsername(currentUsername);
    }

    UtilLocalStorage.itemRemove(this.sessionKey);
    UtilLocalStorage.itemRemove(this.rolePermissionsKey);
    this.sessionSignal.set(null);
    this.rolePermissionsSignal.set(null);
  }

  get isLoggedIn(): boolean {
    return Boolean(this.sessionSignal()?.token?.access_token);
  }

  loggedInCheckSync(): boolean {
    return this.isLoggedIn;
  }

  get accessToken(): string | undefined {
    return this.sessionSignal()?.token?.access_token;
  }

  get refreshToken(): string | undefined {
    return this.sessionSignal()?.token?.refresh_token;
  }

  getAccessTokenOrThrow(): string {
    const token = this.accessToken;
    if (!token) throw new Error('Missing access_token in session');
    return token;
  }

  get currentUser(): AuthenticatedUser | undefined {
    return this.sessionSignal()?.user;
  }

  getUserId(): number | undefined {
    const id = this.currentUser?.user_id;
    return typeof id === 'number' ? id : undefined;
  }

  getUserIdOrThrow(): number {
    const id = this.getUserId();
    if (!id) throw new Error('Missing user_id in session.user');
    return id;
  }

  getEmployeeId(): number | undefined {
    const emp = this.currentUser?.employee_id;
    return typeof emp === 'number' ? emp : undefined;
  }

  getEmployeeIdOrThrow(): number {
    const emp = this.getEmployeeId();
    if (!emp) throw new Error('Missing employee_id in session.user');
    return emp;
  }

  get currentRoleName(): string {
    return String(this.currentUser?.role?.name ?? '').trim();
  }

  get currentRoleId(): number | undefined {
    const rid = this.currentUser?.role?.role_id;
    return typeof rid === 'number' ? rid : undefined;
  }

  get menu(): MenuItem[] {
    return this.sessionSignal()?.menu ?? [];
  }

  getUserDisplayName(): string {
    const u = this.currentUser;
    return String(u?.username ?? u?.email ?? '').trim();
  }

  private saveLastUsername(username: string): void {
    UtilLocalStorage.itemSet<string>(this.lastUserKey, username, false);
  }

  getLastUsername(): string | undefined {
    return UtilLocalStorage.item<string>(this.lastUserKey, false);
  }

  clearLastUsername(): void {
    UtilLocalStorage.itemRemove(this.lastUserKey);
  }
}
